/**
 * <notify-me-dialog>
 *
 * Wraps the "Notify me when available" dialog (snippets/notify-me-modal.liquid).
 * Opens when any [data-notify-me-trigger] button (rendered above sold-out
 * variants in snippets/option-value.liquid) is clicked, and submits the
 * subscriber's email to the `/apps/notify-me/subscribe` app proxy endpoint.
 */
class NotifyMeDialog extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.form = this.querySelector('[data-notify-me-form]');
    this.variants = JSON.parse(this.querySelector('[data-notify-me-variants]').textContent);

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-notify-me-trigger]');
      if (!trigger) return;

      this.open(trigger.dataset.variantId);
    });

    this.querySelector('[data-notify-me-close]').addEventListener('click', () => this.dialog.close());

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });

    this.form.addEventListener('submit', (event) => this.handleSubmit(event));
  }

  open(variantId) {
    const variant = this.variants.find((candidate) => String(candidate.id) === String(variantId));
    if (!variant) return;

    this.resetMessage();
    this.renderVariant(variant);
    this.renderSizeOptions(variant);
    this.dialog.showModal();
  }

  renderVariant(variant) {
    this.querySelector('[data-notify-me-variant-id]').value = variant.id;
    this.querySelector('[data-notify-me-price]').textContent = variant.price;
    this.querySelector('[data-notify-me-color]').textContent = variant.options[0] || '';
    this.querySelector('[data-notify-me-size]').textContent = variant.options[variant.options.length - 1] || '';

    const image = this.querySelector('[data-notify-me-image]');
    if (variant.image) image.src = variant.image;
  }

  renderSizeOptions(variant) {
    // Filter variants to show only sold-out (unavailable) sizes in the popup selection list
    const groupKey = variant.options.slice(0, -1).join(' / ');
    const siblings = this.variants.filter((candidate) => candidate.options.slice(0, -1).join(' / ') === groupKey);
    const soldOutSiblings = siblings.filter((candidate) => !candidate.available);

    const wrapper = this.querySelector('[data-notify-me-sizes]');
    const container = this.querySelector('[data-notify-me-size-options]');

    if (soldOutSiblings.length <= 1) {
      wrapper.hidden = true;
      return;
    }

    container.innerHTML = '';

    soldOutSiblings.forEach((sibling) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = sibling.options[sibling.options.length - 1];
      button.className = 'notify-me-dialog__size-option';
      button.disabled = false; // Sold-out sizes are active/selectable to register notifications
      button.setAttribute('aria-pressed', String(sibling.id === variant.id));

      if (sibling.id === variant.id) {
        button.classList.add('is-selected');
      }

      button.addEventListener('click', () => {
        this.renderVariant(sibling);

        container.querySelectorAll('.notify-me-dialog__size-option').forEach((option) => {
          const isSelected = option === button;
          option.classList.toggle('is-selected', isSelected);
          option.setAttribute('aria-pressed', String(isSelected));
        });
      });

      container.appendChild(button);
    });

    wrapper.hidden = false;
  }

  resetMessage() {
    const message = this.querySelector('[data-notify-me-message]');
    message.hidden = true;
    message.textContent = '';
    message.removeAttribute('data-state');
  }

  async handleSubmit(event) {
    event.preventDefault();

    const message = this.querySelector('[data-notify-me-message]');
    const submitButton = this.querySelector('[data-notify-me-submit]');
    const formData = new FormData(this.form);

    submitButton.disabled = true;
    this.resetMessage();

    try {
      const response = await fetch('https://notify-me-backend.shipping-qatar-extension.workers.dev/apps/notify-me/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.get('email'),
          product_id: formData.get('product_id'),
          variant_id: formData.get('variant_id'),
        }),
      });

      if (!response.ok) throw new Error('Request failed');

      message.textContent = "You're on the list! We'll email you when this is back in stock.";
      message.dataset.state = 'success';
      message.hidden = false;
      this.form.reset();

      const variant = this.variants.find((candidate) => String(candidate.id) === String(formData.get('variant_id')));
      if (variant) this.renderVariant(variant);
    } catch (error) {
      message.textContent = 'Something went wrong. Please try again.';
      message.dataset.state = 'error';
      message.hidden = false;
    } finally {
      submitButton.disabled = false;
    }
  }
}

customElements.define('notify-me-dialog', NotifyMeDialog);
